// Set priority (L2): reorder a station's queue, move a build to another
// station, or assign it to a technician. Otherwise technicians take the next one.
import { useState } from 'react';

import type { BuildSummaryResponse } from '../../api/generated/BuildSummaryResponse';
import { useBuildActions, useBuilds, useOrderQueue, useStations } from '../../api/queries';
import { canBuild, useSession } from '../../auth/session';
import { Button, EmptyState, ErrorPanel, Loading, PageHeader, StateBadge, TextField } from '../../design/components';
import { formatDate } from '../../design/format';

export function move<T>(items: readonly T[], from: number, to: number): T[] {
    const next = [...items];
    const [item] = next.splice(from, 1);
    if (item !== undefined) next.splice(to, 0, item);
    return next;
}

export function QueueOrderPage() {
    const stations = useStations();
    const [station, setStation] = useState('');
    const firstEnabled = stations.data?.find((candidate) => candidate.enabled)?.workCenter ?? '';
    const active = station === '' ? firstEnabled : station;
    const builds = useBuilds({ stations: active ? [active] : [], states: ['Ready', 'InProgress', 'OnHold'] });
    const others = (stations.data ?? [])
        .filter((candidate) => candidate.enabled && candidate.workCenter !== active)
        .map((candidate) => candidate.workCenter);
    return (
        <section className="sf-stack" aria-labelledby="queue-order-heading">
            <PageHeader id="queue-order-heading" title="Set priority" />
            <div className="sf-field" style={{ maxWidth: 320 }}>
                <label htmlFor="queue-station">Station</label>
                <select
                    id="queue-station"
                    className="sf-select"
                    value={active}
                    onChange={(event) => {
                        setStation(event.target.value);
                    }}
                >
                    {stations.data
                        ?.filter((candidate) => candidate.enabled)
                        .map((candidate) => (
                            <option key={candidate.workCenter} value={candidate.workCenter}>
                                {candidate.workCenter}
                            </option>
                        ))}
                </select>
            </div>
            {builds.isPending ? <Loading what="the queue" /> : null}
            {builds.isError ? <ErrorPanel error={builds.error} /> : null}
            {builds.data?.length === 0 ? <EmptyState>Nothing queued at {active}.</EmptyState> : null}
            {builds.data && builds.data.length > 0 ? (
                <QueueList
                    key={`${active}:${builds.data.map((build) => build.buildId).join('|')}`}
                    station={active}
                    builds={builds.data}
                    others={others}
                />
            ) : null}
        </section>
    );
}

/** A station's queue as the lead reorders it, starting from its saved order. */
function QueueList({
    station,
    builds,
    others,
}: {
    station: string;
    builds: readonly BuildSummaryResponse[];
    others: readonly string[];
}) {
    const session = useSession();
    const order = useOrderQueue();
    const [ids, setIds] = useState<string[]>(() => builds.map((build) => build.buildId));
    const byId = new Map(builds.map((build) => [build.buildId, build]));
    const canAct = canBuild(session);
    return (
        <>
            <ol className="sf-stack" style={{ paddingLeft: 0, listStyle: 'none' }}>
                {ids.map((id, index) => {
                    const build = byId.get(id);
                    return build ? (
                        <li key={id} className="sf-card">
                            <QueueRow
                                build={build}
                                position={index + 1}
                                canAct={canAct}
                                stations={others}
                                onUp={
                                    index === 0
                                        ? undefined
                                        : () => {
                                              setIds(move(ids, index, index - 1));
                                          }
                                }
                                onDown={
                                    index === ids.length - 1
                                        ? undefined
                                        : () => {
                                              setIds(move(ids, index, index + 1));
                                          }
                                }
                            />
                        </li>
                    ) : null;
                })}
            </ol>
            {canAct ? (
                <div className="sf-row">
                    <Button
                        variant="primary"
                        disabled={order.isPending}
                        onClick={() => {
                            order.mutate({ station, buildIds: ids });
                        }}
                    >
                        Save this order
                    </Button>
                    {order.isSuccess ? <span role="status">Saved.</span> : null}
                </div>
            ) : null}
            {order.isError ? <ErrorPanel error={order.error} /> : null}
        </>
    );
}

function QueueRow({
    build,
    position,
    canAct,
    stations,
    onUp,
    onDown,
}: {
    build: BuildSummaryResponse;
    position: number;
    canAct: boolean;
    stations: readonly string[];
    onUp: (() => void) | undefined;
    onDown: (() => void) | undefined;
}) {
    const actions = useBuildActions(build.buildId);
    const [technician, setTechnician] = useState(build.assignedTo ?? '');
    const [target, setTarget] = useState('');
    return (
        <div className="sf-stack">
            <div className="sf-row" style={{ justifyContent: 'space-between' }}>
                <span>
                    <strong>#{position}</strong> <span className="sf-mono">{build.assemblyPartNumber}</span>{' '}
                    {build.assemblyName ?? ''} · need by {formatDate(build.needBy)}
                </span>
                <StateBadge state={build.state} />
            </div>
            {canAct ? (
                <div className="sf-row">
                    <Button aria-label={`Move ${build.assemblyPartNumber} up`} disabled={!onUp} onClick={onUp}>
                        ↑
                    </Button>
                    <Button aria-label={`Move ${build.assemblyPartNumber} down`} disabled={!onDown} onClick={onDown}>
                        ↓
                    </Button>
                    <TextField
                        label="Assigned to"
                        value={technician}
                        onChange={setTechnician}
                        placeholder="Sign-in name, or empty"
                    />
                    <Button
                        onClick={() => {
                            actions.assign.mutate(technician.trim() === '' ? null : technician.trim());
                        }}
                    >
                        Assign
                    </Button>
                    {build.state === 'Ready' && stations.length > 0 ? (
                        <>
                            <div className="sf-field">
                                <label htmlFor={`move-${build.buildId}`}>Move to</label>
                                <select
                                    id={`move-${build.buildId}`}
                                    className="sf-select"
                                    value={target}
                                    onChange={(event) => {
                                        setTarget(event.target.value);
                                    }}
                                >
                                    <option value="">Choose a station</option>
                                    {stations.map((name) => (
                                        <option key={name} value={name}>
                                            {name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <Button
                                disabled={target === ''}
                                onClick={() => {
                                    actions.moveTo.mutate(target);
                                }}
                            >
                                Move
                            </Button>
                        </>
                    ) : null}
                </div>
            ) : null}
            {actions.assign.isError ? <ErrorPanel error={actions.assign.error} /> : null}
            {actions.moveTo.isError ? <ErrorPanel error={actions.moveTo.error} /> : null}
        </div>
    );
}
